import React, { useState } from "react";
import { X, Crosshair, Check } from "lucide-react";

// Tap-to-focus picker: sets a focal point (% based) so every display crop centers on the subject.
export default function FocalPointModal({ src, initial = { x: 50, y: 50 }, onDone, onCancel }) {
  const [point, setPoint] = useState(initial);

  const pick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
    setPoint({ x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
  };

  const pos = `${point.x}% ${point.y}%`;

  return (
    <div className="fixed inset-0 z-[1200] bg-[#374151]/50 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="focal-point-modal" onClick={onCancel}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-lg w-full p-6 shadow-xl animate-modal-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 border-b border-[#FCE6E9] pb-3">
          <h3 className="text-base font-extrabold font-heading text-[#374151]">Set Focus Point</h3>
          <button onClick={onCancel} data-testid="focal-cancel" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-[#6B7280] font-semibold mb-3">
          Tap the most important part of the photo (like a face). Wherever this ad appears — big or small — the crop will center on that spot so it never gets cut off.
        </p>

        <div className="relative overflow-hidden rounded-2xl bg-[#111827] cursor-crosshair select-none touch-none" data-testid="focal-stage" onClick={pick}>
          <img src={src} alt="Focus preview" draggable={false} className="w-full max-h-72 object-contain pointer-events-none" />
          <div
            className="absolute w-10 h-10 -ml-5 -mt-5 rounded-full border-[3px] border-[#F8C8DC] bg-[#F8C8DC]/25 shadow-[0_0_0_2px_rgba(55,65,81,0.5)] pointer-events-none transition-all duration-150"
            style={{ left: `${point.x}%`, top: `${point.y}%` }}
            data-testid="focal-marker"
          >
            <Crosshair className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow" />
          </div>
        </div>

        <p className="text-[10px] font-bold text-[#9CA3AF] mt-3 mb-1.5 uppercase tracking-wider">How it will look at every size</p>
        <div className="grid grid-cols-3 gap-2" data-testid="focal-previews">
          {[
            { label: "Wide", ratio: "aspect-video" },
            { label: "Square", ratio: "aspect-square" },
            { label: "Classic", ratio: "aspect-[4/3]" },
          ].map((p) => (
            <div key={p.label}>
              <div className={`${p.ratio} w-full overflow-hidden rounded-xl border border-[#FCE6E9]`}>
                <img src={src} alt="" draggable={false} className="w-full h-full object-cover" style={{ objectPosition: pos }} />
              </div>
              <p className="text-[9px] font-extrabold text-[#9CA3AF] text-center mt-1 uppercase">{p.label}</p>
            </div>
          ))}
        </div>

        <button onClick={() => onDone(point)} data-testid="focal-apply" className="w-full mt-4 px-5 py-3 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-extrabold transition-all flex items-center justify-center gap-2">
          <Check className="w-4 h-4" /> Save Focus Point
        </button>
      </div>
    </div>
  );
}
